import SearchAPI from '../../api/search';
import types from '../mutation-types';

let conversationSearchAbortController = null;

export const initialState = {
  records: [],
  contactRecords: [],
  conversationRecords: [],
  lastConversationSearchRequestId: null,
  conversationSearchQuery: '',
  conversationSearchCompleted: false,
  messageRecords: [],
  articleRecords: [],
  uiFlags: {
    isFetching: false,
    isSearchCompleted: false,
    contact: { isFetching: false },
    conversation: { isFetching: false },
    message: { isFetching: false },
    article: { isFetching: false },
  },
};

export const getters = {
  getConversations(state) {
    return state.records;
  },
  getContactRecords(state) {
    return state.contactRecords;
  },
  getConversationRecords(state) {
    return state.conversationRecords;
  },
  getMessageRecords(state) {
    return state.messageRecords;
  },
  getArticleRecords(state) {
    return state.articleRecords;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
  getConversationSearchQuery(state) {
    return state.conversationSearchQuery || '';
  },
  getConversationSearchCompleted(state) {
    return state.conversationSearchCompleted || false;
  },
};

export const actions = {
  async get({ commit }, { q }) {
    commit(types.SEARCH_CONVERSATIONS_SET, []);
    if (!q) {
      return;
    }
    commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload },
      } = await SearchAPI.get({ q });
      commit(types.SEARCH_CONVERSATIONS_SET, payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  async fullSearch({ commit, dispatch }, payload) {
    const { q, ...filters } = payload;
    if (!q && !Object.keys(filters).length) {
      return;
    }
    commit(types.FULL_SEARCH_SET_UI_FLAG, {
      isFetching: true,
      isSearchCompleted: false,
    });
    try {
      await Promise.all([
        dispatch('contactSearch', { q, ...filters }),
        dispatch('conversationSearch', { q, ...filters }),
        dispatch('messageSearch', { q, ...filters }),
        dispatch('articleSearch', { q, ...filters }),
      ]);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.FULL_SEARCH_SET_UI_FLAG, {
        isFetching: false,
        isSearchCompleted: true,
      });
    }
  },
  async contactSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.contacts({ ...searchParams, page });
      commit(types.CONTACT_SEARCH_SET, data.payload.contacts);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async conversationSearch({ commit, state, rootGetters }, payload) {
    const { page = 1, ...searchParams } = payload;
    const requestId = Date.now();
    const query =
      typeof searchParams.q === 'string' ? searchParams.q.trim() : '';

    // Set state BEFORE aborting previous request to avoid flicker
    commit(types.SET_CONVERSATION_SEARCH_REQUEST_ID, requestId);
    commit(types.SET_CONVERSATION_SEARCH_QUERY, query);
    commit(types.SET_CONVERSATION_SEARCH_COMPLETED, false);
    commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: true });

    // Now abort previous request
    if (conversationSearchAbortController) {
      conversationSearchAbortController.abort();
    }
    conversationSearchAbortController = new AbortController();

    try {
      const { data } = await SearchAPI.conversations(
        { ...searchParams, page },
        { signal: conversationSearchAbortController.signal }
      );

      // Ignore if a newer request was started while this one was in-flight
      if (state.lastConversationSearchRequestId !== requestId) return;

      const conversations = data.payload.conversations || [];

      // Normalizar dados para garantir estrutura igual à lista normal
      const enrichedConversations = conversations.map(conv => {
        const normalized = { ...conv };

        // Flatten meta - copiar campos importantes para o nível superior
        if (normalized.meta) {
          if (!normalized.contact) {
            normalized.contact =
              normalized.meta.contact || normalized.meta.sender;
          }
          if (!normalized.sender && normalized.meta.sender) {
            normalized.sender = normalized.meta.sender;
          }
          if (!normalized.last_message && normalized.meta.last_message) {
            normalized.last_message = normalized.meta.last_message;
          }
          if (
            !normalized.last_non_activity_message &&
            normalized.meta.last_non_activity_message
          ) {
            normalized.last_non_activity_message =
              normalized.meta.last_non_activity_message;
          }
          if (
            !normalized.last_activity_message &&
            normalized.meta.last_activity_message
          ) {
            normalized.last_activity_message =
              normalized.meta.last_activity_message;
          }
        }

        // Garantir que contact existe e tem estrutura completa
        const contactSource =
          normalized.contact ||
          normalized.sender ||
          normalized.meta?.contact ||
          normalized.meta?.sender;
        if (contactSource) {
          normalized.contact = {
            id: contactSource.id || null,
            name: contactSource.name || contactSource.full_name || 'Unknown',
            thumbnail:
              contactSource.thumbnail ||
              contactSource.avatar_url ||
              contactSource.avatar ||
              contactSource.profile_image_url ||
              '',
            availability_status: contactSource.availability_status || 'offline',
            ...contactSource,
          };

          if (
            normalized.contact.id &&
            !normalized.contact.thumbnail &&
            rootGetters['contacts/getContact']
          ) {
            const storeContact = rootGetters['contacts/getContact'](
              normalized.contact.id
            );
            if (storeContact && storeContact.thumbnail) {
              normalized.contact.thumbnail = storeContact.thumbnail;
            }
            if (storeContact && storeContact.name && !normalized.contact.name) {
              normalized.contact.name = storeContact.name;
            }
          }
        }

        return normalized;
      });

      commit(types.CONVERSATION_SEARCH_SET_IF_CURRENT, {
        requestId,
        records: enrichedConversations,
      });
    } catch (error) {
      if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      // Ignore error
    } finally {
      // Only update flags if this is still the current request
      if (state.lastConversationSearchRequestId === requestId) {
        commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: false });
        commit(types.SET_CONVERSATION_SEARCH_COMPLETED, true);
      }
    }
  },
  async messageSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.messages({ ...searchParams, page });
      commit(types.MESSAGE_SEARCH_SET, data.payload.messages);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  // Search messages within a specific conversation
  async messageSearchInConversation(
    { commit },
    { q, conversation_id, page = 1 }
  ) {
    // Always clear previous results when starting a new search in conversation
    commit(types.CLEAR_MESSAGE_SEARCH_RESULTS);
    commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.messages({ q, page, conversation_id });
      const messages = data.payload.messages || [];
      // Replace results instead of appending
      commit(types.MESSAGE_SEARCH_SET_REPLACE, messages);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async articleSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    commit(types.ARTICLE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.articles({ ...searchParams, page });
      commit(types.ARTICLE_SEARCH_SET, data.payload.articles);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.ARTICLE_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async clearSearchResults({ commit }) {
    commit(types.CLEAR_SEARCH_RESULTS);
  },
};

export const mutations = {
  [types.SEARCH_CONVERSATIONS_SET](state, records) {
    state.records = records;
  },
  [types.CONTACT_SEARCH_SET](state, records) {
    const existingIds = new Set(state.contactRecords.map(r => r.id));
    const newRecords = records.filter(r => !existingIds.has(r.id));
    state.contactRecords = [...state.contactRecords, ...newRecords].slice(-500);
  },
  [types.CONVERSATION_SEARCH_SET](state, records) {
    state.conversationRecords = records || [];
  },
  [types.CONVERSATION_SEARCH_SET_IF_CURRENT](state, { requestId, records }) {
    if (state.lastConversationSearchRequestId !== requestId) return;
    state.conversationRecords = records || [];
  },
  [types.SET_CONVERSATION_SEARCH_REQUEST_ID](state, requestId) {
    state.lastConversationSearchRequestId = requestId;
  },
  [types.MESSAGE_SEARCH_SET](state, records) {
    const existingIds = new Set(state.messageRecords.map(r => r.id));
    const newRecords = records.filter(r => !existingIds.has(r.id));
    state.messageRecords = [...state.messageRecords, ...newRecords].slice(-500);
  },
  [types.MESSAGE_SEARCH_SET_REPLACE](state, records) {
    state.messageRecords = records || [];
  },
  [types.CLEAR_MESSAGE_SEARCH_RESULTS](state) {
    state.messageRecords = [];
  },
  [types.ARTICLE_SEARCH_SET](state, records) {
    const existingIds = new Set(state.articleRecords.map(r => r.id));
    const newRecords = records.filter(r => !existingIds.has(r.id));
    state.articleRecords = [...state.articleRecords, ...newRecords].slice(-500);
  },
  [types.SEARCH_CONVERSATIONS_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.FULL_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.CONTACT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.contact = { ...state.uiFlags.contact, ...uiFlags };
  },
  [types.CONVERSATION_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.conversation = { ...state.uiFlags.conversation, ...uiFlags };
  },
  [types.MESSAGE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.message = { ...state.uiFlags.message, ...uiFlags };
  },
  [types.ARTICLE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.article = { ...state.uiFlags.article, ...uiFlags };
  },
  [types.CLEAR_SEARCH_RESULTS](state) {
    state.contactRecords = [];
    state.conversationRecords = [];
    state.lastConversationSearchRequestId = null;
    state.conversationSearchQuery = '';
    state.conversationSearchCompleted = false;
    state.messageRecords = [];
    state.articleRecords = [];
  },
  [types.SET_CONVERSATION_SEARCH_QUERY](state, query) {
    state.conversationSearchQuery = query || '';
  },
  [types.SET_CONVERSATION_SEARCH_COMPLETED](state, value) {
    state.conversationSearchCompleted = !!value;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
